import { useEffect } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { H1, VStack } from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AssignToExistingPurpose } from './components/AssignToExistingPurpose';
import { CreateNewPurposeButton } from './components/CreateNewPurposeButton';

import { PurposeCreationIntent } from '../../../graphql/schema';
import Footer from '../../../Shared/components-v2/footer/Footer';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { usePurposeSelectionStep } from '../FlowConfig';
import { ExistingPurposesDocument } from '../graphql/query.existingPurposes.generated';

interface SelectionToPurposeMap {
  [index: string]: PurposeCreationIntent;
}

const SELECTION_TO_TYPED_PURPOSE: SelectionToPurposeMap = {
  EmergencyFund: PurposeCreationIntent.EmergencyFund,
  Retirement: PurposeCreationIntent.Retirement,
  MajorPurchase: PurposeCreationIntent.MajorPurchase,
};

interface SelectionToUrlBuilder {
  [index: string]: string;
}

export const SELECTION_TO_URL_BUILDER: SelectionToUrlBuilder = {
  EmergencyFund: urlBuilder.appAddEmergencyFundPurpose(),
  Retirement: urlBuilder.appAddRetirementPurpose(),
  MajorPurchase: urlBuilder.appAddMajorPurchasePurpose(),
};

export const AssignmentStep: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { commit, dependencies } = usePurposeSelectionStep('assignment');
  const { data, loading } = useGraphQLQuery(ExistingPurposesDocument, {
    variables: { intent: SELECTION_TO_TYPED_PURPOSE[dependencies.selection.purposeType] },
    throwOnError: true,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!data && !loading) {
      switch (dependencies.selection.purposeType) {
        case 'EmergencyFund':
          void navigate(SELECTION_TO_URL_BUILDER.EmergencyFund);
          break;
        case 'Retirement':
          void navigate(SELECTION_TO_URL_BUILDER.Retirement);
          break;
        case 'MajorPurchase':
          void navigate(SELECTION_TO_URL_BUILDER.MajorPurchase);
          break;
        default:
      }
    }
  }, [data, loading, navigate, dependencies]);

  const handleNextStep = (purposeId: string) => {
    commit({ purposeId });
    // void navigate('/app');
  };

  return (
    <WorkflowLayout pageTitle="">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => (returnTo ? navigate(returnTo) : navigate('/'))} />
      <WorkflowLayout.Content>
        <H1 variant="pageTitle" css={{ mb: 'space.300' }}>
          Do you want to assign this account to your existing goal?
        </H1>
        <VStack css={{ gap: 'space.150', mb: 'space.300' }}>
          {!!data &&
            data.purposes.map(purpose => (
              <AssignToExistingPurpose purpose={purpose} handleAssignment={handleNextStep} key={purpose.id} />
            ))}
        </VStack>
        <CreateNewPurposeButton purposeType={dependencies.selection.purposeType} />
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
